import React, { ElementType, MouseEventHandler, ReactNode } from 'react'

import { Color } from 'themes'
import { SxProps } from '@mui/material'
import { OptionProps } from '../OptionsMenu'

import Badge from 'components_new/atoms/Badge'
import Box from 'components_new/atoms/Box'
import Chip, { ChipProps } from 'components_new/atoms/Chip'
import DefaultListItem from 'components_new/atoms/List/ListItem'
import Icon, { IconProps } from 'components_new/atoms/Icon'
import ListItemButton from 'components_new/atoms/List/ListItemButton'
import ListItemText from 'components_new/atoms/List/ListItemText'
import Skeleton from 'components_new/atoms/Skeleton'
import Tooltip from 'components_new/atoms/Tooltip'

import GenericAvatar, {
  GenericAvatarProps
} from 'components_new/molecules/GenericAvatar'
import OptionsMenuButton from 'components_new/molecules/OptionsMenuButton'

import { useTranslation } from 'language/translator'
import { TextProps } from 'components_new/atoms/Text'

export interface GenericListItemProps {
  action?: 'add' | 'to' // requires onClick || to
  actions?: ReactNode
  AvatarProps?: Pick<GenericAvatarProps, 'badge'>
  body?: ReactNode
  BodyProps?: Omit<TextProps, 'children'>
  border?: boolean
  borderBottom?: boolean
  chip?: ChipProps
  color?: Color
  component?: ElementType<any>
  customTitle?: ReactNode
  disabled?: boolean
  disablePadding?: boolean
  flexTitle?: boolean
  hasHover?: boolean
  icon?: IconProps
  id?: string
  onClick?: (() => void) | MouseEventHandler<HTMLElement>
  onMouseDown?: (() => void) | MouseEventHandler<HTMLElement>
  onMouseMove?: React.MouseEventHandler<HTMLDivElement> | undefined
  options?: OptionProps[]
  role?: 'option'
  title: string
  titleAdornment?: ReactNode
  titleIcon?: IconProps
  titleIconTooltip?: string
  TitleProps?: Omit<TextProps, 'children'>
  to?: string
  size?: 'xsmall' | 'small' | 'medium' | 'large'
  src?: string
  status?: 'error' | 'info' | 'success' | 'warning'
  sx?: SxProps
  tabIndex?: number
  variant?: GenericAvatarProps['variant']
}

const LIST_ITEM_ICON_CLASSNAME = 'ListItemIcon'

const GenericListItem = React.forwardRef(
  (props: GenericListItemProps, ref: any) => {
    const {
      action,
      actions,
      AvatarProps,
      body,
      BodyProps,
      border,
      borderBottom,
      chip,
      color,
      component,
      customTitle,
      disabled,
      disablePadding,
      flexTitle = false,
      hasHover,
      icon,
      id,
      onClick,
      onMouseDown,
      onMouseMove,
      options,
      role,
      title,
      titleAdornment,
      titleIcon,
      titleIconTooltip,
      TitleProps,
      to,
      size,
      src,
      status,
      sx,
      variant
    } = props

    return (
      <ListItemWrapper
        action={action}
        actions={actions}
        border={border}
        borderBottom={borderBottom}
        disablePadding={disablePadding}
        disabled={disabled}
        component={component ?? undefined}
        dense={size === 'xsmall' || size === 'small'}
        hasHover={hasHover}
        id={id}
        onClick={onClick}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        options={options}
        ref={ref}
        role={role}
        sx={sx}
        to={to}
      >
        <Badge variant="dot" invisible={!status} color={status}>
          {icon ? (
            <Icon
              color={'disabled'}
              fontSize={size === 'xsmall' ? 'small' : size}
              sx={{ mr: '0.5em' }}
              {...icon}
            />
          ) : variant || src ? (
            <GenericAvatar
              body={typeof body === 'string' ? body : undefined}
              className={LIST_ITEM_ICON_CLASSNAME}
              color={color}
              size={size}
              src={src}
              sx={{ mr: '0.5em' }}
              title={variant === 'user' && !body ? undefined : title}
              variant={variant}
              {...AvatarProps}
            />
          ) : null}
        </Badge>
        <ListItemText
          className="ListItemText"
          data-openreplay-obscured
          primary={
            <>
              {customTitle ?? title ?? (
                <Skeleton animation={false} width={'12ch'} />
              )}
              {titleIcon ? (
                <Tooltip title={titleIconTooltip}>
                  <Icon
                    sx={{
                      color: 'text.secondary',
                      fontSize: '1em',
                      ml: '0.25em',
                      verticalAlign: '-0.1em'
                    }}
                    {...titleIcon}
                  />
                </Tooltip>
              ) : null}
              {titleAdornment}
            </>
          }
          primaryTypographyProps={{
            variant: size === 'xsmall' ? 'body2' : 'subtitle2',
            noWrap: true,
            lineHeight: size === 'small' ? 1 : undefined,
            display: flexTitle ? 'flex' : undefined,
            alignItems: flexTitle ? 'center' : undefined,
            ...TitleProps
          }}
          secondary={size === 'xsmall' || size === 'small' ? undefined : body}
          secondaryTypographyProps={{
            color: 'text.secondary',
            component: Box,
            noWrap: true,
            ...BodyProps
          }}
          sx={{
            my: disablePadding ? 0 : undefined
          }}
        />
        {chip ? <Chip size={'xsmall'} {...chip} /> : null}
      </ListItemWrapper>
    )
  }
)

interface ListItemWrapperProps {
  action: GenericListItemProps['action']
  actions: GenericListItemProps['actions']
  border: GenericListItemProps['border']
  borderBottom: GenericListItemProps['borderBottom']
  children: ReactNode
  component?: ElementType<any>
  dense?: boolean
  disablePadding: GenericListItemProps['disablePadding']
  disabled: GenericListItemProps['disabled']
  hasHover: GenericListItemProps['hasHover']
  id: GenericListItemProps['id']
  onClick: GenericListItemProps['onClick']
  onMouseDown: GenericListItemProps['onMouseDown']
  onMouseMove: GenericListItemProps['onMouseMove']
  options: GenericListItemProps['options']
  role: GenericListItemProps['role']
  sx: GenericListItemProps['sx']
  to: GenericListItemProps['to']
}

const ListItemWrapper = React.forwardRef(
  (props: ListItemWrapperProps, ref: any) => {
    const {
      action,
      actions,
      border,
      borderBottom,
      children,
      disabled,
      disablePadding,
      hasHover,
      id,
      onClick,
      onMouseDown,
      onMouseMove,
      options,
      role,
      sx,
      to,
      ...rest
    } = props

    const { t } = useTranslation()

    return onClick || to ? (
      <ListItemButton
        disabled={disabled}
        disableGutters={disablePadding}
        href={to}
        id={id}
        onClick={onClick}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        ref={ref}
        role={role}
        sx={{
          ...(borderBottom
            ? {
                '&:not(:last-child)': {
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }
              }
            : null),
          ...(border
            ? {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
              }
            : null),
          ...sx
        }}
        {...rest}
      >
        {children}
        {action ? (
          <Icon
            name={action === 'add' ? 'Add' : 'ArrowForwardIos'}
            sx={{
              color: 'text.disabled',
              fontSize: action === 'add' ? '1.25em' : '1em'
            }}
          />
        ) : null}
      </ListItemButton>
    ) : (
      <DefaultListItem
        disablePadding={disablePadding}
        id={id}
        onMouseMove={onMouseMove}
        sx={{
          ...sx,
          ...(borderBottom
            ? {
                '&:not(:last-child)': {
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }
              }
            : null),
          ...(border
            ? {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
              }
            : null),
          ...(hasHover
            ? {
                '&:hover': {
                  bgcolor: 'action.hover',
                  '& .UserItemActions': {
                    opacity: 1
                  }
                }
              }
            : null)
        }}
        {...rest}
      >
        {children}
        <Box
          className="UserItemActions"
          sx={{ display: 'flex', gap: 0.5, ml: 1, opacity: actions ? 1 : 0 }}
        >
          {actions}
          {options ? (
            <OptionsMenuButton
              options={options}
              size="small"
              tooltip={t('global', 'options')}
            />
          ) : null}
        </Box>
      </DefaultListItem>
    )
  }
)

export default React.memo(GenericListItem)
